import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Copy, Upload } from "lucide-react";
import { API, formatApiError } from "../contexts/AuthContext";
import { copyToClipboard } from "../utils/clipboard";
import Select from "../components/Select";
import { renderCustomFields, validateCustomFields } from "../../components/CustomFieldsRenderer";
import NoRefundsNotice from "../../components/NoRefundsNotice";
import "../lms.css";
import "../portal.css";

const MDCAT_BODIES = [
  "Punjab (UHS)", "Sindh (IBA)", "KPK (KMU)", "Balochistan (BUHMS)", "SZABMU",
];
const SUBJECTS = ["Biology", "Chemistry", "Physics", "English", "Logical Reasoning"];

const PM_TYPE_META = {
  bank:      { name: "Bank Transfer", color: "#0066ff" },
  jazzcash:  { name: "JazzCash",      color: "#e60023" },
  easypaisa: { name: "EasyPaisa",     color: "#00a651" },
  raast:     { name: "Raast",         color: "#5c2d91" },
  other:     { name: "Other",         color: "#5ceeff" },
};

/* ----- Field helper ----- */
const Field = ({ label, hint, children, required }) => (
  <div>
    <label className="lms-label flex items-center gap-1.5">
      {label}
      {required && <span className="text-[#ff9bb0] text-[11px]">*</span>}
    </label>
    {children}
    {hint && <div className="mt-1.5 text-[11px] text-white/40 leading-relaxed">{hint}</div>}
  </div>
);

/* ----- Upload button — streams the file directly to Bunny.net Storage
   via /api/registrations/uploads/image and stores only the returned CDN
   URL in the form. Replaces the earlier FileReader→base64 approach that
   was inflating Mongo docs by ~1 MB per receipt/CNIC image.

   `kind` is a folder hint on the backend ("cnic" or "receipt") so
   uploads land under `registrations/<yyyy-mm>/<kind>-<uuid>.<ext>` on
   Bunny — makes future audits / cost reporting per file-type trivial. */
function UploadBtn({ label, value, onChange, accept = "image/*", kind = "misc" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Guard client-side too — the backend enforces 5 MB but we want to
    // fail fast without wasting the user's mobile data on a doomed POST.
    if (f.size > 5 * 1024 * 1024) {
      setErr("File is over 5 MB — please compress or pick a smaller one.");
      return;
    }
    setBusy(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", kind);
    try {
      const res = await axios.post(`${API}/registrations/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const url = res.data?.url;
      if (!url) throw new Error("Upload succeeded but no URL was returned.");
      onChange(url);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="block cursor-pointer">
      <input type="file" accept={accept} onChange={handle} className="hidden" />
      <div className={`flex items-center gap-3 px-4 py-4 rounded-[14px] border-2 border-dashed transition ${value ? "border-[rgba(45,232,165,0.4)] bg-[rgba(45,232,165,0.04)]" : "border-white/15 hover:border-[rgba(0,229,255,0.3)] bg-white/[0.02]"}`}>
        {value ? <Check size={16} className="text-[#7df5c8]" /> : <Upload size={16} className="text-white/50" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white/85 truncate">{busy ? "Uploading…" : value ? "Uploaded — click to change" : label}</div>
          {!value && <div className="text-[11px] text-white/40 mt-0.5">PNG, JPG up to 5 MB</div>}
          {err && <div className="text-[11px] text-red-400 mt-0.5" data-testid="upload-btn-error">{err}</div>}
        </div>
      </div>
    </label>
  );
}

/* ===== Main form ===== */
export default function RegisterPage() {
  const { slug } = useParams();
  const [batch, setBatch] = useState(null);
  const [batchErr, setBatchErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [regResult, setRegResult] = useState(null);  // { auto_approved, temp_password?, ... }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Effective payment methods to render. We start with whatever the batch
  // returned, then if the batch's source is "global" we hydrate from
  // /api/site/payment-methods (kept as a separate request because the
  // public batch endpoint is sync and we don't want to await inside
  // enrich_batch_for_public on every request).
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [f, setF] = useState({
    first_name: "", last_name: "", guardian_name: "", guardian_relation: "Father",
    whatsapp: "", email: "", gender: "Male",
    cnic_bform: "", cnic_image_url: "",
    mdcat_body: "Punjab (UHS)",
    matric_obtained: "", matric_total: 1100,
    student_type: "fresher",
    fsc_part1_percentage: "",
    fsc2_obtained: "", fsc2_total: 1100,
    subscription_type: "all_subjects",
    selected_subjects: [],
    selected_addon_id: "",
    payment_receipt_url: "",
    payment_datetime: "",
  });
  // Admin-defined extra fields live in their own dict so they don't pollute
  // the strongly-typed core form. Keys mirror `field.key` from the editor.
  const [extra, setExtra] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setExtraField = (k, v) => setExtra((p) => ({ ...p, [k]: v }));

  const [movedTo, setMovedTo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/batches/${slug}`)
      .then((r) => {
        // iter 223 — a retired session slug (…-2026) resolves to the live
        // intake so old links, QR codes and ads keep working.
        if (r.data?.moved_to) { setMovedTo(r.data.moved_to); return; }
        setBatch(r.data);
        // Resolve effective payment methods: batch's own list wins; otherwise
        // load the global fallback from site_settings.
        const batchMethods = Array.isArray(r.data?.payment_methods) ? r.data.payment_methods : [];
        if (r.data?.payment_methods_source === "batch" && batchMethods.length > 0) {
          setPaymentMethods(batchMethods);
        } else {
          axios.get(`${API}/site/payment-methods`)
            .then((g) => setPaymentMethods(Array.isArray(g.data) ? g.data : []))
            .catch(() => setPaymentMethods([]));
        }
      })
      .catch((e) => setBatchErr(formatApiError(e.response?.data?.detail) || "Batch not found"));
  }, [slug]);

  const copyValue = (val, label) => copyToClipboard(val, label || "Copied");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    // Resolve effective score config (admin can disable / mark optional
    // individual score fields per-batch). Default ON when not configured.
    const scCfg = batch?.score_config || {};
    const matricSC = scCfg.matric || { enabled: true, required: true };
    const fsc1SC = scCfg.fsc1 || { enabled: true, required: true };
    const fsc2SC = scCfg.fsc2 || { enabled: true, required: true };
    const matricEnabled = matricSC.enabled !== false;
    const fsc1Enabled = fsc1SC.enabled !== false;
    const fsc2Enabled = fsc2SC.enabled !== false;

    // Resolve per-batch visibility/required overrides for core payment
    // fields. The admin can hide OR mark them optional via the "Public
    // form fields" editor on the batch. Both toggles must be honoured
    // client-side or the form will still block/require a hidden field.
    const coreCfg = batch?.form_config || {};
    const receiptCfg = coreCfg.payment_receipt || {};
    const payDtCfg = coreCfg.payment_datetime || {};
    const receiptHidden = !!receiptCfg.hidden;
    const payDtHidden = !!payDtCfg.hidden;
    const receiptRequired = receiptCfg.required !== undefined ? !!receiptCfg.required : true;
    const payDtRequired = payDtCfg.required !== undefined ? !!payDtCfg.required : true;

    if (!f.cnic_image_url) return setErr("Please upload your CNIC / B-form image.");
    if (!receiptHidden && receiptRequired && !f.payment_receipt_url) {
      return setErr("Please upload your payment receipt.");
    }
    if (!payDtHidden && payDtRequired && !f.payment_datetime) {
      return setErr("Please enter the date & time of your payment.");
    }
    if (f.student_type === "fresher" && fsc1Enabled && fsc1SC.required !== false && !f.fsc_part1_percentage) {
      return setErr("F.Sc Part 1 percentage is required for freshers.");
    }
    if (f.student_type === "repeater" && fsc2Enabled && fsc2SC.required !== false && !f.fsc2_obtained) {
      return setErr("F.Sc Part 2 marks are required for repeaters.");
    }
    if (f.subscription_type === "per_subject" && f.selected_subjects.length === 0) return setErr("Select at least one subject.");

    // Validate admin-configured extra fields (required ones).
    const customFields = batch?.registration_form_fields || [];
    const missing = validateCustomFields(customFields, extra);
    if (missing) return setErr(`"${missing}" is required.`);

    // Build a numeric value safely — empty string or non-numeric → null
    // instead of NaN. JSON.stringify converts NaN to null, but Pydantic
    // then can't tell "this field is absent" from "this field has a
    // numeric value that happens to be invalid" — historically this
    // caused the cryptic "Input should be a valid integer" error when
    // the field was disabled by admin but the form still tried to
    // submit it.
    const numOrNull = (raw, parser = parseFloat) => {
      const n = parser(raw);
      return Number.isFinite(n) ? n : null;
    };
    const scoreObj = (obtainedRaw, totalRaw, enabled) => {
      if (!enabled) return null;
      const obt = numOrNull(obtainedRaw, parseFloat);
      if (obt === null) return null;
      const tot = numOrNull(totalRaw, parseInt);
      return { obtained: obt, total: tot };
    };

    setBusy(true);
    try {
      const payload = {
        first_name: f.first_name, last_name: f.last_name,
        guardian_name: f.guardian_name, guardian_relation: f.guardian_relation,
        whatsapp: f.whatsapp, email: f.email, gender: f.gender,
        cnic_bform: f.cnic_bform, cnic_image_url: f.cnic_image_url,
        mdcat_body: f.mdcat_body,
        matric: scoreObj(f.matric_obtained, f.matric_total || matricSC.max || 1100, matricEnabled),
        student_type: f.student_type,
        fsc_part1_percentage: fsc1Enabled && f.fsc_part1_percentage
          ? numOrNull(f.fsc_part1_percentage, parseFloat)
          : null,
        fsc_part2: scoreObj(f.fsc2_obtained, f.fsc2_total || fsc2SC.max || 1100, fsc2Enabled),
        batch_id: batch.id,
        subscription_type: f.subscription_type,
        selected_subjects: f.selected_subjects,
        selected_addon_id: f.selected_addon_id || null,
        payment_receipt_url: f.payment_receipt_url,
        payment_datetime: f.payment_datetime,
        extra_fields: Object.keys(extra).length ? extra : null,
      };
      const r = await axios.post(`${API}/registrations`, payload);
      setRegResult(r.data || {});
      setSubmitted(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (batchErr) {
    return (
      <div className="lms-root min-h-screen flex items-center justify-center px-6">
        <div className="lms-glass-strong p-10 text-center max-w-md">
          <div className="lms-display text-white text-2xl mb-2">Batch unavailable</div>
          <p className="text-white/60 text-sm">{batchErr}</p>
          <Link to="/" className="lms-btn lms-btn-ghost mt-6 inline-flex" data-testid="register-back-home">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  // iter 223 — retired session slug → forward to the live intake.
  if (movedTo) {
    return <Navigate to={`/register/${movedTo}`} replace />;
  }

  // ── Iter 172 · Free-batch routing enforcement ──────────────────────
  // If the admin has flipped this batch to `is_free`, the entire paid
  // approval workflow no longer applies — the student MUST go through
  // the free-batch flow (promo code + instant credentials + referral
  // ladder). Redirect BEFORE any paid-batch UI renders.
  if (batch?.is_free) {
    return <Navigate to={`/free/${slug}`} replace />;
  }

  if (!batch) {
    return <div className="lms-root min-h-screen flex items-center justify-center"><div className="lms-shimmer h-8 w-64" /></div>;
  }

  if (submitted) {
    const autoApproved = !!regResult?.auto_approved;
    return (
      <div className="lms-root min-h-screen flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lms-glass-strong p-10 text-center max-w-lg" data-testid="reg-success">
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5"
            style={{
              background: autoApproved ? "rgba(45,232,165,0.20)" : "rgba(45,232,165,0.15)",
              border: `1px solid ${autoApproved ? "rgba(45,232,165,0.7)" : "rgba(45,232,165,0.5)"}`,
            }}
          >
            <Check size={26} className="text-[#7df5c8]" />
          </div>
          <div className="lms-mono text-[#7df5c8] mb-2">
            {autoApproved ? "— Registration successful" : "— Application received"}
          </div>
          <h1 className="lms-display text-white text-3xl md:text-4xl leading-tight mb-4">
            {autoApproved
              ? <>Welcome aboard, {f.first_name}.</>
              : <>Masha&apos;Allah, {f.first_name}.</>}
          </h1>
          {autoApproved ? (
            <>
              <p className="text-white/75 text-sm leading-relaxed mb-5" data-testid="reg-success-auto">
                You&apos;re in <strong className="text-white">{batch.name}</strong>. Your login credentials have been sent to your registered email address <strong className="text-[#5ceeff]">{f.email}</strong>. Please check your inbox (and spam folder just in case).
              </p>
              {regResult?.temp_password && (
                <div className="rounded-[14px] p-4 mb-6 text-left"
                     style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.35)" }}
                     data-testid="reg-success-creds">
                  <div className="lms-mono text-[10px] tracking-[0.16em] text-[#5ceeff] mb-2">TEMPORARY CREDENTIALS</div>
                  <div className="text-[12.5px] text-white/70">Email</div>
                  <div className="font-mono text-[13.5px] text-white mb-2 break-all">{f.email}</div>
                  <div className="text-[12.5px] text-white/70">Password (change on first login)</div>
                  <div className="font-mono text-[13.5px] text-white break-all">{regResult.temp_password}</div>
                </div>
              )}
              <Link
                to="/login"
                className="lms-btn lms-btn-primary"
                data-testid="reg-success-login"
              >
                Log in to your portal <ArrowRight size={14} />
              </Link>
            </>
          ) : (
            <>
              <p className="text-white/65 text-sm leading-relaxed mb-6">
                Your application for <strong className="text-white">{batch.name}</strong> is under review. Our admin team will verify your payment and activate your Logician portal access within 24 hours. You&apos;ll receive an email at <strong className="text-[#5ceeff]">{f.email}</strong> with your login credentials.
              </p>
              <Link to="/" className="lms-btn lms-btn-primary" data-testid="register-done">Back to site <ArrowRight size={14} /></Link>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="lms-root min-h-screen">
      <div aria-hidden className="fixed inset-0 lms-grain pointer-events-none" />

      <div className="relative z-10 px-5 md:px-10 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition" data-testid="register-back">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <Link to="/login" className="text-sm text-white/60 hover:text-white transition" data-testid="register-login-link">Already enrolled? Sign in</Link>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-10 py-8 md:py-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="lms-mono text-[#5ceeff] mb-3">— Apply for admission</div>
          <h1 className="lms-display text-white text-3xl md:text-5xl leading-[1.05]">{batch.name}</h1>
          <p className="text-white/55 text-sm md:text-base mt-3 max-w-xl leading-relaxed">{batch.description || "Fill this form carefully. Our admin verifies each application before activating your portal access."}</p>
        </motion.div>

        <form onSubmit={submit} className="space-y-8" data-testid="registration-form">
          {/* Identity */}
          <Section title="Identity" step="01">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="First name" required>
                <input className="lms-input" required value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-first-name" />
              </Field>
              <Field label="Last name" required>
                <input className="lms-input" required value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-last-name" />
              </Field>
              <Field label="Guardian's name" required>
                <input className="lms-input" required value={f.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} data-testid="reg-guardian-name" />
              </Field>
              <Field label="Guardian's relation" required>
                <Select
                  value={f.guardian_relation}
                  onChange={(v) => set("guardian_relation", v)}
                  options={[{ value: "Father", label: "Father" }, { value: "Mother", label: "Mother" }]}
                  searchable={false}
                  testid="reg-guardian-relation"
                />
              </Field>
              <Field label="Gender" required>
                <Select
                  value={f.gender}
                  onChange={(v) => set("gender", v)}
                  options={[{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }]}
                  searchable={false}
                  testid="reg-gender"
                />
              </Field>
              <Field label="CNIC / B-form number" hint="Numbers only — dashes optional." required>
                <input className="lms-input" required value={f.cnic_bform} onChange={(e) => set("cnic_bform", e.target.value)} data-testid="reg-cnic" />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Upload CNIC / B-form picture" required>
                <UploadBtn label="Drop or tap to upload CNIC / B-form" value={f.cnic_image_url} onChange={(v) => set("cnic_image_url", v)} kind="cnic" />
              </Field>
            </div>
          </Section>

          {/* Contact & login */}
          <Section title="Contact & portal login" step="02">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="WhatsApp number" hint="To access exclusive WhatsApp features." required>
                <input className="lms-input" type="tel" required value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="reg-whatsapp" />
              </Field>
              <Field label="Email" hint="To access your student portal." required>
                <input className="lms-input" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
              </Field>
            </div>
          </Section>

          {/* MDCAT academic */}
          <Section title="Academic profile" step="03">
            <Field label="MDCAT conducting body" required>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {MDCAT_BODIES.map((b) => (
                  <button type="button" key={b} onClick={() => set("mdcat_body", b)}
                    className={`px-3 py-3 rounded-[14px] border text-xs text-left transition ${f.mdcat_body === b ? "bg-[rgba(0,229,255,0.08)] border-[rgba(0,229,255,0.4)] text-[#5ceeff]" : "bg-white/[0.02] border-white/10 text-white/75 hover:border-white/20"}`}
                    data-testid={`reg-body-${b.split(" ")[0].toLowerCase()}`}>
                    {b}
                  </button>
                ))}
              </div>
            </Field>

            {(() => {
              const sc = batch.score_config?.matric || { enabled: true, required: true, format: "marks", max: 1100 };
              if (!sc.enabled) return null;
              return (
                <ScoreField id="matric" label="Matric score" cfg={sc}
                  obtained={f.matric_obtained} onObtained={(v) => set("matric_obtained", v)}
                  total={f.matric_total} onTotal={(v) => set("matric_total", v)}
                  testid="reg-matric" />
              );
            })()}

            <div className="mt-6">
              <div className="lms-mono mb-3">— You are a</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { k: "fresher", label: "MDCAT Fresher", sub: "First MDCAT attempt" },
                  { k: "repeater", label: "MDCAT Repeater", sub: "Retaking MDCAT" },
                  { k: "improver_both", label: "Improving F.Sc + MDCAT", sub: "Both together" },
                ].map((o) => (
                  <button type="button" key={o.k} onClick={() => set("student_type", o.k)}
                    className={`p-4 rounded-[14px] border text-left transition ${f.student_type === o.k ? "bg-[rgba(0,229,255,0.08)] border-[rgba(0,229,255,0.4)]" : "bg-white/[0.02] border-white/10 hover:border-white/20"}`}
                    data-testid={`reg-type-${o.k}`}
                  >
                    <div className="text-sm text-white font-medium">{o.label}</div>
                    <div className="lms-mono mt-1 text-white/45">{o.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {(f.student_type === "fresher" || f.student_type === "improver_both") && (() => {
              const sc = batch.score_config?.fsc1 || { enabled: true, required: true, format: "percentage", max: 100 };
              if (!sc.enabled) return null;
              return (
                <div className="mt-5">
                  <ScoreField id="fsc1" label="F.Sc Part 1 score" cfg={sc}
                    obtained={f.fsc_part1_percentage} onObtained={(v) => set("fsc_part1_percentage", v)}
                    total={f.fsc_part1_total || sc.max} onTotal={(v) => set("fsc_part1_total", v)}
                    testid="reg-fsc1" />
                </div>
              );
            })()}
            {(f.student_type === "repeater" || f.student_type === "improver_both") && (() => {
              const sc = batch.score_config?.fsc2 || { enabled: true, required: true, format: "marks", max: 1100 };
              if (!sc.enabled) return null;
              return (
                <div className="mt-5">
                  <ScoreField id="fsc2" label="F.Sc Part 2 score" cfg={sc}
                    obtained={f.fsc2_obtained} onObtained={(v) => set("fsc2_obtained", v)}
                    total={f.fsc2_total} onTotal={(v) => set("fsc2_total", v)}
                    testid="reg-fsc2" />
                </div>
              );
            })()}
          </Section>

          {/* Subscription */}
          <Section title="Choose your subscription" step="04">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reg-plans-grid">
              {/* Admin-configured plans (dynamic). We key off the plan id so a
                  selection persists across re-renders; sub_id="all_subjects"
                  / "per_subject" stay as legacy back-compat for downstream
                  ingestion (saved_application + admin export). */}
              {(batch.plans || []).map((plan) => {
                const subType = plan.per_subject ? "per_subject" : "all_subjects";
                const selected = f.subscription_type === subType && (f.selected_plan_id || "") === (plan.id || "");
                return (
                  <button type="button" key={plan.id}
                    onClick={() => {
                      set("subscription_type", subType);
                      set("selected_plan_id", plan.id);
                    }}
                    className={`p-5 rounded-[14px] border text-left transition ${selected ? "bg-[rgba(0,229,255,0.08)] border-[rgba(0,229,255,0.4)]" : "bg-white/[0.02] border-white/10 hover:border-white/20"}`}
                    data-testid={`reg-plan-${(plan.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {(plan.features || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {plan.features.map((b, i) => (
                          <span key={i} className="lms-mono text-[10px] tracking-[0.18em] text-[#5ceeff] px-1.5 py-0.5 rounded bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.25)]">
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-white font-medium">{plan.label}</div>
                    {plan.sub && <div className="text-white/55 text-[12px] mt-1">{plan.sub}</div>}
                    {plan.price ? (
                      <div className="text-[#5ceeff] text-2xl font-semibold mt-2">{plan.price}</div>
                    ) : plan.per_subject && Number.isFinite(batch.price_per_subject) ? (
                      <div className="text-white text-2xl font-semibold mt-2">PKR {Number(batch.price_per_subject).toLocaleString()} <span className="text-sm text-white/50">/ subject</span></div>
                    ) : !plan.per_subject && Number.isFinite(batch.price_one_time) ? (
                      <div className="text-[#5ceeff] text-2xl font-semibold mt-2">PKR {Number(batch.price_one_time).toLocaleString()}</div>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {f.subscription_type === "per_subject" && (
              <div className="mt-4">
                <Field label="Select subjects" required>
                  <div className="flex flex-wrap gap-2">
                    {SUBJECTS.map((s) => {
                      const active = f.selected_subjects.includes(s);
                      return (
                        <button type="button" key={s}
                          onClick={() => set("selected_subjects", active ? f.selected_subjects.filter(x => x !== s) : [...f.selected_subjects, s])}
                          className={`px-4 py-2 rounded-full border text-xs transition ${active ? "bg-[rgba(0,229,255,0.1)] border-[rgba(0,229,255,0.5)] text-[#5ceeff]" : "bg-white/[0.02] border-white/10 text-white/70 hover:border-white/20"}`}
                          data-testid={`reg-subj-${s.toLowerCase()}`}
                        >{s}</button>
                      );
                    })}
                  </div>
                </Field>
              </div>
            )}

            {/* Add-on plan picker — only rendered when admin has configured
                price_addons on the batch. Base tile is always shown first so
                the student can revert to the plain plan. Selecting an add-on
                simply sets `f.selected_addon_id`; the price column below
                recomputes the live total. */}
            {Array.isArray(batch.price_addons) && batch.price_addons.length > 0 && (
              <div className="mt-6">
                <div className="lms-label mb-2.5">Choose your plan</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Base plan tile */}
                  {(() => {
                    const isAll = f.subscription_type === "all_subjects";
                    const base = isAll ? (batch.price_one_time || 0) : (batch.price_per_subject || 0);
                    const active = !f.selected_addon_id;
                    return (
                      <button type="button"
                        onClick={() => set("selected_addon_id", "")}
                        className={`p-5 rounded-[14px] border text-left transition ${active ? "bg-[rgba(0,229,255,0.08)] border-[rgba(0,229,255,0.4)]" : "bg-white/[0.02] border-white/10"}`}
                        data-testid="reg-addon-base">
                        <div className="lms-mono text-[#5ceeff]/80 mb-1 text-[10px] uppercase tracking-[0.16em]">Base plan</div>
                        <div className="text-white font-medium">{batch.base_plan_label || "Standard"}</div>
                        <div className={`${active ? "text-[#5ceeff]" : "text-white"} text-2xl font-semibold mt-2`}>
                          PKR {base.toLocaleString()}
                          {!isAll && <span className="text-sm text-white/50"> / subject</span>}
                        </div>
                      </button>
                    );
                  })()}
                  {/* Add-on tiles */}
                  {[...batch.price_addons].sort((a, b) => (a.order || 0) - (b.order || 0)).map((ad) => {
                    const isAll = f.subscription_type === "all_subjects";
                    const base = isAll ? (batch.price_one_time || 0) : (batch.price_per_subject || 0);
                    const delta = isAll ? (ad.price_delta_one_time || 0) : (ad.price_delta_per_subject || 0);
                    const total = base + delta;
                    const active = f.selected_addon_id === ad.id;
                    return (
                      <button type="button" key={ad.id}
                        onClick={() => set("selected_addon_id", ad.id)}
                        className={`relative p-5 rounded-[14px] border text-left transition ${active ? "bg-[rgba(167,139,250,0.10)] border-[rgba(167,139,250,0.55)]" : "bg-white/[0.02] border-white/10"}`}
                        data-testid={`reg-addon-${ad.id}`}>
                        {/* "+ PKR" delta pill — top-right */}
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold"
                          style={{
                            background: active ? "rgba(167,139,250,0.25)" : "rgba(255,255,255,0.06)",
                            color: active ? "#d8b4fe" : "rgba(255,255,255,0.6)",
                            border: `1px solid ${active ? "rgba(167,139,250,0.5)" : "rgba(255,255,255,0.12)"}`,
                          }}>
                          + PKR {delta.toLocaleString()}
                        </div>
                        <div className="lms-mono mb-1 text-[10px] uppercase tracking-[0.16em]"
                          style={{ color: active ? "#c4b5fd" : "rgba(255,255,255,0.5)" }}>
                          Upgrade
                        </div>
                        <div className="text-white font-medium pr-16">{ad.name}</div>
                        {ad.description && (
                          <div className="text-white/55 text-[12px] mt-1 leading-relaxed">{ad.description}</div>
                        )}
                        <div className={`${active ? "text-[#d8b4fe]" : "text-white"} text-2xl font-semibold mt-3`}>
                          PKR {total.toLocaleString()}
                          {!isAll && <span className="text-sm text-white/50"> / subject</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </Section>

          {/* Admin-configured extra questions for this batch. Rendered in
              the order the admin set in the batch form. Hidden when no
              custom fields are defined. */}
          {(batch?.registration_form_fields || []).length > 0 && (
            <Section title="A few more questions" step="04½">
              {renderCustomFields({
                fields: batch.registration_form_fields,
                values: extra,
                onChange: setExtraField,
                theme: "dark",
                idPrefix: "lms-reg-cf",
              })}
            </Section>
          )}

          {/* Payment */}
          <Section title="Payment" step="05">
            {paymentMethods.length === 0 ? (
              <div className="lms-glass p-5 mb-4 text-center text-sm text-white/65">
                Payment instructions are being configured by the LogicsWallah team. Please contact admin if you don't see a method here within the next few minutes.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="reg-payment-methods">
                {paymentMethods.map((m, i) => (
                  <PaymentMethodCard key={m.id || i} method={m} onCopy={copyValue} />
                ))}
              </div>
            )}

            {paymentMethods.some((m) => m.type === "bank") && (
              <div className="mb-4 p-3 rounded-lg bg-[rgba(255,203,71,0.06)] border border-[rgba(255,203,71,0.2)] text-[11px] text-white/75 leading-relaxed">
                <strong className="text-[#ffd970]">EasyPaisa / JazzCash users:</strong> Open your wallet → Money Transfer → Bank Transfer → select the bank shown above → enter the account number → pay your admission fee.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!(batch?.form_config?.payment_receipt?.hidden) && (
                <Field
                  label={batch?.form_config?.payment_receipt?.label_override || "Upload transaction receipt"}
                  hint={batch?.form_config?.payment_receipt?.hint_override || "Screenshot of successful transfer."}
                  required={batch?.form_config?.payment_receipt?.required !== false}
                >
                  <UploadBtn label="Drop or tap to upload receipt" value={f.payment_receipt_url} onChange={(v) => set("payment_receipt_url", v)} kind="receipt" />
                </Field>
              )}
              {!(batch?.form_config?.payment_datetime?.hidden) && (
                <Field
                  label={batch?.form_config?.payment_datetime?.label_override || "Date & exact time of payment"}
                  hint={batch?.form_config?.payment_datetime?.hint_override}
                  required={batch?.form_config?.payment_datetime?.required !== false}
                >
                  <input
                    type="datetime-local"
                    className="lms-input"
                    required={batch?.form_config?.payment_datetime?.required !== false}
                    value={f.payment_datetime}
                    onChange={(e) => set("payment_datetime", e.target.value)}
                    data-testid="reg-paydate"
                  />
                </Field>
              )}
            </div>
          </Section>

          {err && <div className="p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm" data-testid="reg-error">{err}</div>}

          {/* Policy notice — uniform across every registration regardless
              of batch. Shown immediately above Submit so it's the last
              thing the student reads before tapping. */}
          <NoRefundsNotice theme="dark" />

          <button type="submit" disabled={busy} className="lms-btn lms-btn-primary w-full justify-center h-12 text-[15px]" data-testid="reg-submit">
            {busy ? "Submitting…" : "I accept the refund policy — Submit application"}
            {!busy && <ArrowRight size={16} />}
          </button>

          <p className="text-center text-[11px] text-white/40">
            After submission, our admin team will verify your payment within 24 hours and email your login credentials to <strong>{f.email || "your email"}</strong>.
          </p>
        </form>
      </div>
    </div>
  );
}

const Section = ({ title, step, children }) => (
  <motion.section initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
    <div className="flex items-center gap-3 mb-5">
      <div className="lms-mono text-[#ffd970]">{step}</div>
      <div className="flex-1 lms-divider" />
      <div className="lms-display text-white text-lg md:text-xl">{title}</div>
    </div>
    <div>{children}</div>
  </motion.section>
);

/**
 * ScoreField — renders Matric / F.Sc Part 1 / F.Sc Part 2 in either
 * "marks out of N" or "percentage" mode based on admin config. Keeps the
 * obtained-value input shape consistent (always a number) so downstream
 * ingestion code doesn't have to branch.
 */
function ScoreField({ id, label, cfg, obtained, onObtained, total, onTotal, testid }) {
  const isPercentage = cfg.format === "percentage";
  if (isPercentage) {
    return (
      <Field label={`${label} (percentage)`} hint="Example: 82.5" required={!!cfg.required}>
        <input className="lms-input" type="number" step="0.1" min="0" max="100"
               value={obtained} onChange={(e) => onObtained(e.target.value)}
               required={!!cfg.required}
               data-testid={`${testid}-percentage`} />
      </Field>
    );
  }
  return (
    <div className="grid grid-cols-[1fr_auto] gap-3" data-testid={`${testid}-marks-row`}>
      <Field label={`${label} obtained`} required={!!cfg.required}>
        <input className="lms-input" type="number" min="0"
               required={!!cfg.required}
               value={obtained}
               onChange={(e) => onObtained(e.target.value)}
               data-testid={`${testid}-obtained`} />
      </Field>
      <Field label={`Out of (max ${cfg.max || 1100})`} required={!!cfg.required}>
        <input className="lms-input w-[110px]" type="number" min="1"
               value={total || cfg.max || 1100}
               onChange={(e) => onTotal(e.target.value)}
               data-testid={`${testid}-total`} />
      </Field>
    </div>
  );
}

/**
 * PaymentMethodCard — public-facing render of one admin-configured method.
 * Visual structure is identical for every method type so applicants see a
 * consistent set of cards regardless of whether the method is a bank, a
 * JazzCash wallet, or Raast. The colored top strip cues the brand instantly.
 */
function PaymentMethodCard({ method, onCopy }) {
  const meta = PM_TYPE_META[method.type] || PM_TYPE_META.other;
  return (
    <div className="lms-glass overflow-hidden" data-testid={`payment-method-card-${method.type}`}>
      <div className="px-4 py-2 flex items-center justify-between"
           style={{ background: `linear-gradient(90deg, ${meta.color}26 0%, transparent 100%)`,
                    borderBottom: `1px solid ${meta.color}33` }}>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-white/65">{meta.name}</div>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {method.label && <div className="text-sm font-semibold text-white">{method.label}</div>}
        {method.account_title && (
          <div>
            <div className="lms-mono text-[10px] text-white/50">Account title</div>
            <div className="text-sm text-white">{method.account_title}</div>
          </div>
        )}
        {method.number && (
          <div>
            <div className="lms-mono text-[10px] text-white/50">Account / wallet number</div>
            <div className="flex items-center gap-2 mt-1 p-2 rounded-lg bg-white/5 border border-white/10">
              <code className="flex-1 text-[#5ceeff] tracking-wider font-mono text-sm break-all">{method.number}</code>
              <button type="button"
                      onClick={() => onCopy(method.number, "Account number")}
                      className="w-8 h-8 rounded-md hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition"
                      data-testid={`copy-number-${method.id || method.type}`}
                      aria-label="Copy account number">
                <Copy size={12} />
              </button>
            </div>
          </div>
        )}
        {method.iban && (
          <div>
            <div className="lms-mono text-[10px] text-white/50">IBAN</div>
            <div className="flex items-center gap-2 mt-1 p-2 rounded-lg bg-white/5 border border-white/10">
              <code className="flex-1 text-white/85 tracking-wider font-mono text-xs break-all">{method.iban}</code>
              <button type="button"
                      onClick={() => onCopy(method.iban, "IBAN")}
                      className="w-8 h-8 rounded-md hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition"
                      data-testid={`copy-iban-${method.id || method.type}`}
                      aria-label="Copy IBAN">
                <Copy size={12} />
              </button>
            </div>
          </div>
        )}
        {method.note && (
          <div className="text-[11px] text-white/55 italic">{method.note}</div>
        )}
      </div>
    </div>
  );
}
